import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Empty, Skeleton, Spin } from 'antd';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import useVideoMessageResource from '../Hooks/useVideoMessageResource';
import { GALLERY_BATCH_SIZE } from '@/utilities/helpers/galleryUtils';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { generateVideoThumbnailWithTimeout } from '@/utilities/helpers/videoThumbnailUtils';
import './Gallery.less';

const VIDEO_THUMBNAIL_TIMEOUT_MS = 12000;

const GalleryMessages = ({ hubRecord }) => {
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const { useruuid } = useMemo(() => currentUser, [currentUser]);
  // const accountUuid = hubRecord?.useruuid;
  const [thumbnails, setThumbnails] = useState({});
  const [generatingThumbnails, setGeneratingThumbnails] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(GALLERY_BATCH_SIZE);

  const { data: messages = [], isLoading, error } = useVideoMessageResource({ useruuid: useruuid });

  const visibleMessages = messages.slice(0, visibleCount);

  useEffect(() => {
    visibleMessages.forEach((messageResource) => {
      const resourceId = messageResource.resourceuuid;
      const mediaUrl = messageResource.url || messageResource.downloadurl;

      if (
        !resourceId ||
        !mediaUrl ||
        thumbnails[resourceId] ||
        generatingThumbnails.has(resourceId)
      ) {
        return;
      }

      setGeneratingThumbnails((previous) => new Set(previous).add(resourceId));
      generateVideoThumbnailWithTimeout(mediaUrl, VIDEO_THUMBNAIL_TIMEOUT_MS, 0.2)
        .then((thumbnail) => {
          setThumbnails((previous) => ({ ...previous, [resourceId]: thumbnail }));
        })
        .catch(() => {
          setThumbnails((previous) => ({ ...previous, [resourceId]: null }));
        })
        .finally(() => {
          setGeneratingThumbnails((previous) => {
            const next = new Set(previous);
            next.delete(resourceId);
            return next;
          });
        });
    });
  }, [visibleMessages, thumbnails, generatingThumbnails]);

  useEffect(() => {
    const hasMoreMessages = visibleCount < messages.length;

    if (!hasMoreMessages) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          setVisibleCount((previousCount) =>
            Math.min(previousCount + GALLERY_BATCH_SIZE, messages.length)
          );
        });
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    const sentinelElement = document.getElementById('gallery-messages-scroll-sentinel');
    if (sentinelElement) {
      observer.observe(sentinelElement);
    }

    return () => observer.disconnect();
  }, [messages.length, visibleCount]);

  if (isLoading) {
    return (
      <div className="gallery-videos-container">
        <div className="gallery-videos-grid">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="gallery-video-item">
              <Skeleton.Button active block className="gallery-skeleton-video-thumb" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || messages.length === 0) {
    return (
      <div className="gallery-videos-container">
        <Empty description={trans('NODATAFOUND')} />
      </div>
    );
  }

  return (
    <div className="gallery-videos-container">
      <div className="gallery-videos-grid">
        {visibleMessages.map((messageResource) => {
          const resourceId = messageResource.resourceuuid;
          const thumbnail = thumbnails[resourceId];
          const isGenerating = generatingThumbnails.has(resourceId);

          return (
            <div key={resourceId} className="gallery-video-item">
              <div className="gallery-video-thumbnail-wrapper">
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt={messageResource.title || trans('MESSAGE')}
                    className="gallery-video-thumbnail"
                  />
                ) : isGenerating ? (
                  <div className="gallery-thumbnail-loading">
                    <Spin size="small" />
                  </div>
                ) : (
                  <div className="gallery-thumbnail-placeholder gallery-thumbnail-fallback" />
                )}
                <div className="gallery-video-play-overlay">
                  <div className="gallery-play-icon">
                    <span>▶</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {visibleCount < messages.length && (
        <div id="gallery-messages-scroll-sentinel" style={{ height: 1 }} />
      )}
    </div>
  );
};

GalleryMessages.propTypes = {
  hubRecord: PropTypes.object,
};

export default GalleryMessages;
